import { Shell } from "@/components/layout/Shell";
import { usePecas } from "@/hooks/use-data";
import type { Peca } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Package, Plus, X, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import { VEICULO_TIPO_LABELS, VEICULO_TIPO_COLORS } from "@/lib/constants";

const UF_LIST = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

type CreateMode = null | "single" | "batch";

function UfSelector({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (ufs: string[]) => void;
}) {
  const toggle = (uf: string) => {
    if (selected.includes(uf)) {
      onChange(selected.filter((u) => u !== uf));
    } else {
      onChange([...selected, uf]);
    }
  };
  const selectAll = () => onChange([...UF_LIST]);
  const clearAll = () => onChange([]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>UFs onde a peça se aplica *</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={selectAll}
            data-testid="button-select-all-ufs"
          >
            Todos
          </Button>
          <span className="text-xs text-muted-foreground">|</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearAll}
            data-testid="button-clear-all-ufs"
          >
            Limpar
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="uf-selector">
        {UF_LIST.map((uf) => (
          <button
            key={uf}
            type="button"
            onClick={() => toggle(uf)}
            className={`px-2 py-1 text-xs rounded border transition-colors ${
              selected.includes(uf)
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-input hover:bg-accent"
            }`}
            data-testid={`uf-toggle-${uf}`}
          >
            {uf}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {selected.length} UF(s) selecionada(s)
      </p>
    </div>
  );
}

function CreatePecaForm({
  mode,
  session,
  onClose,
}: {
  mode: "single" | "batch";
  session: any;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [nome, setNome] = useState("");
  const [tipoVeiculo, setTipoVeiculo] = useState("");
  const [selectedUfs, setSelectedUfs] = useState<string[]>([]);
  const [sequencia, setSequencia] = useState<string>("");
  const [batchText, setBatchText] = useState("");
  const [batchTipo, setBatchTipo] = useState("");
  const [batchUfs, setBatchUfs] = useState<string[]>([]);

  const singleMutation = useMutation({
    mutationFn: async (data: {
      nome: string;
      tipoVeiculo: string;
      ufs: string[];
      sequencia?: number;
    }) => {
      const res = await fetch("/api/pecas", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar peça");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.pecas.list.path] });
      toast({
        title: "Peça criada",
        description: "Peça cadastrada com sucesso.",
      });
      setNome("");
      setTipoVeiculo("");
      setSelectedUfs([]);
      setSequencia("");
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const batchMutation = useMutation({
    mutationFn: async (data: {
      pecas: {
        nome: string;
        tipoVeiculo: string;
        ufs: string[];
        sequencia?: number;
      }[];
    }) => {
      const res = await fetch("/api/pecas/batch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar peças");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.pecas.list.path] });
      toast({
        title: "Peças criadas",
        description: `${data.length} peça(s) cadastrada(s) com sucesso.`,
      });
      setBatchText("");
      setBatchTipo("");
      setBatchUfs([]);
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSingleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome || !tipoVeiculo || selectedUfs.length === 0) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha nome, tipo e selecione ao menos uma UF.",
        variant: "destructive",
      });
      return;
    }
    singleMutation.mutate({
      nome: nome
        .replace(/[\s\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]/g, " ")
        .trim()
        .toUpperCase(),
      tipoVeiculo,
      ufs: selectedUfs,
      sequencia: sequencia ? parseInt(sequencia, 10) : undefined,
    });
  };

  const parseBatchLine = (
    line: string,
  ): { nome: string; sequencia?: number } => {
    const trimmed = line.trim();
    // Regex: número no início (opcional) seguido de espaço e texto
    const match = trimmed.match(/^(\d+)\s+(.+)$/);
    if (match) {
      return {
        sequencia: parseInt(match[1], 10),
        nome: match[2],
      };
    }
    return { nome: trimmed };
  };

  const handleBatchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!batchText.trim() || !batchTipo || batchUfs.length === 0) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha tipo, UFs e nomes das peças.",
        variant: "destructive",
      });
      return;
    }
    const lines = batchText
      .split("\n")
      .map((n) => n.trim())
      .filter(Boolean);
    if (lines.length === 0) return;
    batchMutation.mutate({
      pecas: lines.map((line) => {
        const { nome, sequencia } = parseBatchLine(line);
        return {
          nome: nome
            .replace(/[\s\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]/g, " ")
            .trim()
            .toUpperCase(),
          tipoVeiculo: batchTipo,
          ufs: batchUfs,
          sequencia,
        };
      }),
    });
  };

  return (
    <Card className="border-2 border-primary/20 shadow-lg mb-6">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-base">
          {mode === "single" ? "Nova Peça" : "Cadastro em Lote"}
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid="button-close-peca-form"
        >
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {mode === "single" ? (
          <form onSubmit={handleSingleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome da Peça *</Label>
                <Input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Para-choque dianteiro"
                  required
                  data-testid="input-peca-nome"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Veículo *</Label>
                <Select value={tipoVeiculo} onValueChange={setTipoVeiculo}>
                  <SelectTrigger data-testid="select-peca-tipo">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(VEICULO_TIPO_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sequência (opcional)</Label>
              <Input
                type="number"
                value={sequencia}
                onChange={(e) => setSequencia(e.target.value)}
                placeholder="Deixar vazio para auto-incrementar"
                min="1"
                data-testid="input-peca-sequencia"
              />
              <p className="text-xs text-muted-foreground">
                Se deixado vazio, a sequência será atribuída automaticamente
              </p>
            </div>
            <UfSelector selected={selectedUfs} onChange={setSelectedUfs} />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={singleMutation.isPending}
                data-testid="button-submit-peca"
              >
                {singleMutation.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Cadastrar
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleBatchSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Veículo *</Label>
              <Select value={batchTipo} onValueChange={setBatchTipo}>
                <SelectTrigger data-testid="select-batch-tipo">
                  <SelectValue placeholder="Selecione o tipo para todas as peças" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VEICULO_TIPO_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <UfSelector selected={batchUfs} onChange={setBatchUfs} />
            <div className="space-y-2">
              <Label>Nomes das Peças (uma por linha) *</Label>
              <Textarea
                value={batchText}
                onChange={(e) => setBatchText(e.target.value)}
                placeholder={
                  "Para-choque dianteiro\nPara-choque traseiro\nCapô\nPara-lama direito\nPara-lama esquerdo"
                }
                rows={8}
                className="font-mono text-sm"
                data-testid="textarea-batch-pecas"
              />
              <p className="text-xs text-muted-foreground">
                {batchText.split("\n").filter((l) => l.trim()).length} peça(s)
                serão cadastradas
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={batchMutation.isPending}
                data-testid="button-submit-batch"
              >
                {batchMutation.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Cadastrar Lote
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function TipoSection({
  tipo,
  items,
  defaultUf,
  isOwner,
  session,
}: {
  tipo: string;
  items: Peca[];
  defaultUf: string;
  isOwner: boolean;
  session: any;
}) {
  const { toast } = useToast();
  const availableUfs = [...new Set(items.flatMap((p) => p.ufs ?? []))].sort();

  const [selectedUf, setSelectedUf] = useState<string>(
    availableUfs.includes(defaultUf) ? defaultUf : (availableUfs[0] ?? ""),
  );

  const filtered = (
    selectedUf ? items.filter((p) => p.ufs?.includes(selectedUf)) : items
  ).sort((a, b) => {
    const seqA =
      selectedUf && a.ufSequencias
        ? (a.ufSequencias as Record<string, number>)[selectedUf]
        : undefined;
    const seqB =
      selectedUf && b.ufSequencias
        ? (b.ufSequencias as Record<string, number>)[selectedUf]
        : undefined;
    if (seqA !== undefined && seqB !== undefined) return seqA - seqB;
    return a.nome.localeCompare(b.nome, "pt-BR");
  });

  const deletePecaMutation = useMutation({
    mutationFn: async ({ id, uf }: { id: string; uf: string }) => {
      const res = await fetch(`/api/pecas/${id}?uf=${encodeURIComponent(uf)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao excluir peça");
      }
      return res.json() as Promise<{ result: "deleted" | "updated" }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.pecas.list.path] });
      if (data.result === "updated") {
        toast({
          title: "UF removida",
          description: `${selectedUf} removida da peça com sucesso.`,
        });
      } else {
        toast({
          title: "Peça excluída",
          description: "Peça removida com sucesso.",
        });
      }
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteLoteMutation = useMutation({
    mutationFn: async (uf: string) => {
      const res = await fetch(
        `/api/pecas/tipo/${tipo}?uf=${encodeURIComponent(uf)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${session?.access_token}` },
        },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao excluir lote");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.pecas.list.path] });
      const nextUf = availableUfs.find((u) => u !== selectedUf) ?? "";
      setSelectedUf(nextUf);
      toast({
        title: "Lote atualizado",
        description: `${selectedUf} removida de todas as peças de "${VEICULO_TIPO_LABELS[tipo] || tipo}".`,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Card className="border-none shadow-md">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <Badge
            variant="secondary"
            className={
              VEICULO_TIPO_COLORS[tipo] || "bg-slate-100 text-slate-700"
            }
          >
            {VEICULO_TIPO_LABELS[tipo] || tipo}
          </Badge>
          <Select value={selectedUf} onValueChange={setSelectedUf}>
            <SelectTrigger
              className="w-[90px] h-7 text-xs"
              data-testid={`select-uf-${tipo}`}
            >
              <SelectValue placeholder="UF" />
            </SelectTrigger>
            <SelectContent>
              {availableUfs.map((uf) => (
                <SelectItem key={uf} value={uf}>
                  {uf}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {filtered.length} peça(s)
          </span>
          {isOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={deleteLoteMutation.isPending}
                  data-testid={`button-delete-lote-${tipo}`}
                >
                  {deleteLoteMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  <span className="ml-1 text-xs">Excluir lote</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Remover {selectedUf} do lote?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Isso vai remover <strong>{selectedUf}</strong> de todas as{" "}
                    <strong>{filtered.length} peça(s)</strong> de{" "}
                    <strong>{VEICULO_TIPO_LABELS[tipo] || tipo}</strong>{" "}
                    visíveis agora. Peças que só existem em {selectedUf} serão
                    excluídas; as demais apenas perderão este estado.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive hover:bg-destructive/90"
                    onClick={() => deleteLoteMutation.mutate(selectedUf)}
                  >
                    Remover {selectedUf}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhuma peça para {selectedUf} neste tipo.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((peca) => {
              const seq =
                selectedUf && peca.ufSequencias
                  ? (peca.ufSequencias as Record<string, number>)[selectedUf]
                  : undefined;
              return (
                <div
                  key={peca.id}
                  className="group flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  data-testid={`item-peca-${peca.id}`}
                >
                  <span className="text-xs font-mono text-slate-400 w-10 text-right flex-shrink-0">
                    {seq !== undefined ? String(seq).padStart(3, "0") : "—"}
                  </span>
                  <span className="text-sm font-medium text-slate-700 flex-1">
                    {peca.nome}
                  </span>
                  {!peca.ativo && (
                    <Badge variant="outline" className="text-xs">
                      Inativa
                    </Badge>
                  )}
                  {isOwner && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6 text-slate-400 hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                          disabled={deletePecaMutation.isPending}
                          data-testid={`button-delete-peca-${peca.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {peca.ufs && peca.ufs.length > 1
                              ? `Remover ${selectedUf} desta peça?`
                              : "Excluir peça?"}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {peca.ufs && peca.ufs.length > 1 ? (
                              <>
                                A peça <strong>{peca.nome}</strong> ainda estará
                                disponível nos outros {peca.ufs.length - 1}{" "}
                                estado(s). Apenas <strong>{selectedUf}</strong>{" "}
                                será removido.
                              </>
                            ) : (
                              <>
                                Tem certeza que deseja excluir{" "}
                                <strong>{peca.nome}</strong>? Esta ação não pode
                                ser desfeita.
                              </>
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive hover:bg-destructive/90"
                            onClick={() =>
                              deletePecaMutation.mutate({
                                id: peca.id,
                                uf: selectedUf,
                              })
                            }
                          >
                            {peca.ufs && peca.ufs.length > 1
                              ? "Remover UF"
                              : "Excluir"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Pecas() {
  const { data: pecas, isLoading } = usePecas();
  const { profile, session } = useAuth();
  const isOwner = profile?.roles?.includes("owner");
  const [createMode, setCreateMode] = useState<CreateMode>(null);

  const userUf = profile?.empresa?.uf ?? profile?.estado ?? "";

  const grouped = Object.keys(VEICULO_TIPO_LABELS).reduce(
    (acc, tipo) => {
      const items = (pecas ?? []).filter((p) => p.tipoVeiculo === tipo);
      if (items.length > 0) acc[tipo] = items;
      return acc;
    },
    {} as Record<string, Peca[]>,
  );

  return (
    <Shell>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1
            className="text-3xl font-bold font-display text-slate-900"
            data-testid="text-page-title"
          >
            Peças
          </h1>
          <p className="text-muted-foreground mt-1">
            Lista de peças por tipo de veículo e estado
          </p>
        </div>
        {isOwner && !createMode && (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => setCreateMode("single")}
              data-testid="button-add-peca"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Peça
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreateMode("batch")}
              data-testid="button-add-peca-batch"
            >
              <Plus className="w-4 h-4 mr-2" />
              Lote
            </Button>
          </div>
        )}
      </div>

      {createMode && (
        <CreatePecaForm
          mode={createMode}
          session={session}
          onClose={() => setCreateMode(null)}
        />
      )}

      {isLoading ? (
        <div className="space-y-4">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
        </div>
      ) : Object.keys(grouped).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(grouped).map(([tipo, items]) => (
            <TipoSection
              key={tipo}
              tipo={tipo}
              items={items}
              defaultUf={userUf}
              isOwner={isOwner ?? false}
              session={session}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          Nenhuma peça encontrada
        </div>
      )}
    </Shell>
  );
}
